import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Currency } from '../../../models/currency';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { LookupTableComponent } from '../../shared/lookup-table/lookup-table.component';
import { LutService } from '../../../services/lut.service';
import { LookupDataService } from '../../../services/lookupDataService.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-currency-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    NgxSpinnerModule
  ],
  templateUrl: './currency-list.component.html',
  styleUrl: './currency-list.component.scss'
})
export class CurrencyListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  currencies = signal<Currency[]>([]);

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private lutService: LutService,
    private lookupDataService: LookupDataService,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadCurrencies();
  }

  loadCurrencies(): void {
    this.spinnerService.show();
    this.lutService.getCurrencies().subscribe({
      next: (data) => {
        // Sort alphabetically by name before setting
        const sortedData = this.sortCurrencies(data);
        this.currencies.set(sortedData);
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading currencies:', error);
        this.spinnerService.hide();
      }
    });
  }

  private sortCurrencies(currencies: Currency[]): Currency[] {
    return [...currencies].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }

  onCreateCurrency(): void {
    this.lookupTable.startAdd();
  }

  onItemSelected(currency: Currency): void {
  }

  onEditCurrency(currency: Currency): void {
  }

  onDeleteCurrency(currency: Currency): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(currency.name)
      .subscribe(result => {
        if (result) {
          // Store original list for rollback in case of error
          const originalCurrencies = this.currencies();

          // 1. Optimistically remove from local state immediately
          this.currencies.update(currencies => currencies.filter(c => c.id !== currency.id));

          // 2. Call backend API
          this.spinnerService.show();
          this.lutService.deleteCurrency(currency.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Currency deleted successfully');
              // 3. Update cache so other components get updated data
              this.lookupDataService.updateTableData('currencies', this.currencies());
            },
            error: (error) => {
              console.error('Error deleting currency:', error);
              this.spinnerService.hide();
              // 4. On error: Restore original list
              this.currencies.set(originalCurrencies);
              this.toast.error(`<strong>Failed to delete Currency:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: Currency, newName: string} | {item: Currency, newValue: string, columnKey: string}): void {
    // Store original item for rollback in case of error
    const originalItem = { ...event.item };

    // Prepare updated item
    let updatedItem: Currency;
    if ('newName' in event) {
      // Legacy format - simple name update
      updatedItem = { ...event.item, name: event.newName };
    } else {
      // New format - update specific column
      updatedItem = { ...event.item, [event.columnKey]: event.newValue };
    }

    // 1. Optimistically update local state immediately and re-sort
    this.currencies.update(currencies => {
      const updated = currencies.map(c => c.id === event.item.id ? updatedItem : c);
      return this.sortCurrencies(updated);
    });

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.updateCurrency(event.item.id, updatedItem).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Currency updated successfully');
        // 3. On success: Update with server response and re-sort to maintain alphabetical order
        this.currencies.update(currencies => {
          const updated = currencies.map(c => c.id === event.item.id ? response : c);
          return this.sortCurrencies(updated);
        });
        // 4. Update cache so other components get updated data
        this.lookupDataService.updateTableData('currencies', this.currencies());
      },
      error: (error) => {
        console.error('Error updating currency:', error);
        this.spinnerService.hide();
        // 4. On error: Rollback to original state
        this.currencies.update(currencies =>
          currencies.map(c => c.id === event.item.id ? originalItem : c)
        );
        this.toast.error(`<strong>Failed to update Currency:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    // Generate temporary ID for optimistic update
    const tempId = crypto.randomUUID();
    const newCurrency: Currency = { id: tempId, name: event['name'], systemReserved: false };

    // 1. Optimistically add to local state immediately and sort
    this.currencies.update(currencies => this.sortCurrencies([...currencies, newCurrency]));

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.createCurrency({ name: event['name'], systemReserved: false }).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Currency created successfully');
        // 3. On success: Replace temp item with server response and re-sort to maintain alphabetical order
        this.currencies.update(currencies => {
          const updated = currencies.map(c => c.id === tempId ? response : c);
          return this.sortCurrencies(updated);
        });
        // 4. Update cache so other components get updated data
        this.lookupDataService.updateTableData('currencies', this.currencies());
      },
      error: (error) => {
        console.error('Error creating currency:', error);
        this.spinnerService.hide();
        // 4. On error: Remove the temp item
        this.currencies.update(currencies =>
          currencies.filter(c => c.id !== tempId)
        );
        this.toast.error(`<strong>Failed to create Currency:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }
}
